import { create } from "zustand";

/**
 * Notification store state interface
 * Tracks pending licenses and requests for the notification bell
 */
interface NotificationsState {
  // Pending items counts
  pendingLicensesCount: number;      // For clients: licenses to claim
  rejectedRequestsCount: number;     // For clients: rejected license requests
  pendingRequestsCount: number;      // For artists: incoming license requests
  expiredLicensesCount: number;      // For both sides: expired/revoked licenses
  expiringSoonCount: number;         // For both sides: licenses approaching expiry
  licenseClaimedCount: number;       // For artists: pending licenses now claimed

  // Modal visibility (controlled externally via bell click)
  showPendingLicensesModal: boolean;
  showPendingRequestsModal: boolean;
  showExpiredLicensesModal: boolean;
  showExpiringSoonModal: boolean;
  showLicenseClaimedModal: boolean;

  // Actions
  setPendingLicensesCount: (count: number) => void;
  setRejectedRequestsCount: (count: number) => void;
  setPendingRequestsCount: (count: number) => void;
  setExpiredLicensesCount: (count: number) => void;
  setExpiringSoonCount: (count: number) => void;
  setLicenseClaimedCount: (count: number) => void;
  setShowPendingLicensesModal: (show: boolean) => void;
  setShowPendingRequestsModal: (show: boolean) => void;
  setShowExpiredLicensesModal: (show: boolean) => void;
  setShowExpiringSoonModal: (show: boolean) => void;
  setShowLicenseClaimedModal: (show: boolean) => void;
  openNotifications: () => void;
  clearCounts: () => void;

  // Computed
  getTotalCount: () => number;
}

/**
 * Notifications store using Zustand
 * Manages pending license/request counts and modal visibility
 */
export const useNotificationsStore = create<NotificationsState>()((set, get) => ({
  // Initial state
  pendingLicensesCount: 0,
  rejectedRequestsCount: 0,
  pendingRequestsCount: 0,
  expiredLicensesCount: 0,
  expiringSoonCount: 0,
  licenseClaimedCount: 0,
  showPendingLicensesModal: false,
  showPendingRequestsModal: false,
  showExpiredLicensesModal: false,
  showExpiringSoonModal: false,
  showLicenseClaimedModal: false,

  setPendingLicensesCount: (count) => set({ pendingLicensesCount: count }),
  setRejectedRequestsCount: (count) => set({ rejectedRequestsCount: count }),
  setPendingRequestsCount: (count) => set({ pendingRequestsCount: count }),
  setExpiredLicensesCount: (count) => set({ expiredLicensesCount: count }),
  setExpiringSoonCount: (count) => set({ expiringSoonCount: count }),
  setLicenseClaimedCount: (count) => set({ licenseClaimedCount: count }),

  setShowPendingLicensesModal: (show) => set({ showPendingLicensesModal: show }),
  setShowPendingRequestsModal: (show) => set({ showPendingRequestsModal: show }),
  setShowExpiredLicensesModal: (show) => set({ showExpiredLicensesModal: show }),
  setShowExpiringSoonModal: (show) => set({ showExpiringSoonModal: show }),
  setShowLicenseClaimedModal: (show) => set({ showLicenseClaimedModal: show }),

  // Open notifications (called from bell click)
  openNotifications: () => {
    const state = get();
    if (state.pendingLicensesCount > 0 || state.rejectedRequestsCount > 0) {
      set({ showPendingLicensesModal: true });
    }
    if (state.pendingRequestsCount > 0) {
      set({ showPendingRequestsModal: true });
    }
    if (state.expiredLicensesCount > 0) {
      set({ showExpiredLicensesModal: true });
    }
    if (state.expiringSoonCount > 0) {
      set({ showExpiringSoonModal: true });
    }
    if (state.licenseClaimedCount > 0) {
      set({ showLicenseClaimedModal: true });
    }
  },

  // Clear all counts (on org change or logout)
  clearCounts: () => set({
    pendingLicensesCount: 0,
    rejectedRequestsCount: 0,
    pendingRequestsCount: 0,
    expiredLicensesCount: 0,
    expiringSoonCount: 0,
    licenseClaimedCount: 0,
  }),

  // Get total notification count
  getTotalCount: () => {
    const state = get();
    return (
      state.pendingLicensesCount +
      state.rejectedRequestsCount +
      state.pendingRequestsCount +
      state.expiredLicensesCount +
      state.expiringSoonCount +
      state.licenseClaimedCount
    );
  },
}));
